import {LoginResponse, OidcSecurityService} from 'angular-auth-oidc-client';
import {catchError, Observable, of, switchMap} from 'rxjs';

import {accessTokenNeedsRenewal} from './access-token-expiry';

export interface RestoreSessionOptions {
  /** /callback: checkAuth() processes the authorization response; never retry it via refresh. */
  isCallback?: boolean;
  /** Called when a stale session cannot be renewed. Returns the result to hand back. */
  onFailure: (result: LoginResponse) => Observable<LoginResponse>;
}

/**
 * checkAuth() and, when the stored access token has expired, renew it with the refresh token.
 *
 * The library reports an expired stored token as "not authenticated" and does not refresh it,
 * so a page loaded after the token's lifetime looks signed out although the session is alive.
 * A fresh tab without a refresh token never contacts IAM here.
 */
export function restoreSession(oidc: OidcSecurityService, options: RestoreSessionOptions): Observable<LoginResponse> {
  return oidc.checkAuth().pipe(
    switchMap(result => {
      const staleToken = !!result.accessToken && accessTokenNeedsRenewal(result.accessToken);
      if (options.isCallback || (result.isAuthenticated && !staleToken)) {
        return of(result);
      }
      const failed = () => options.onFailure(result);
      // A fresh session has no refresh token. Let the route guard (or the login button) start login.
      return oidc.getRefreshToken().pipe(
        switchMap(token => {
          if (!token) return staleToken ? failed() : of(result);
          // The library's cached expires_at can disagree with the JWT exp.
          // IncludingServer would skip renewal if it still considers the session valid.
          const renewal = staleToken && result.isAuthenticated
            ? oidc.forceRefreshSession()
            : oidc.checkAuthIncludingServer();
          return renewal.pipe(
            switchMap(renewed => renewed.isAuthenticated && !accessTokenNeedsRenewal(renewed.accessToken)
              ? of(renewed) : failed()),
            catchError(failed),
          );
        }),
      );
    }),
  );
}
